import { useEffect, useState } from 'react';
import { Box, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { formatCrmDateTime } from '../../kanban-board/formatters/crm-datetime';
import { CrmScheduleService } from '../../kanban-board/services/crm-schedule.service';
import { LeadScheduleResponse } from '../../kanban-board/services/responses/lead-schedule.response';
import { ScheduleOutcome } from '../../kanban-board/constants/crm-schedule-outcome';
import { LeadBlock } from './lead-block';
import { LeadActionModal } from './lead-action-modal';
import { useGoogleCalendar } from '../../agenda/hooks/use-google-calendar.hook';
import { AgendaMeetOption, calendarWarning } from '../../agenda/components/agenda-meet-option';

interface Props {
    leadId: string;
    version: number;
    onChanged: () => void;

    /// Horário escolhido na grade ao lado (formato datetime-local); ao mudar, abre o formulário.
    suggestedTime?: string;
}

export const LeadActions = ({ leadId, version, onChanged, suggestedTime }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [actions, setActions] = useState<LeadScheduleResponse[] | null>(null);
    const [creating, setCreating] = useState(false);
    const [text, setText] = useState('');
    const [when, setWhen] = useState('');
    const [createMeet, setCreateMeet] = useState(false);
    const [attempted, setAttempted] = useState(false);
    const [editing, setEditing] = useState<LeadScheduleResponse | null>(null);
    const { usable: googleReady } = useGoogleCalendar();

    const load = () =>
        // Mesmo detalhe da rota do histórico: /paginated faz parte dela.
        new CrmScheduleService()
            .getAll(`${leadId}/paginated`, { currentPage: 1, recordsPerPage: 100 })
            .then((res) => setActions(res.data.data?.records ?? []))
            .catch((err) => {
                setActions([]);
                showAlert(apiErrorMessage(err), 'error', 4000);
            });

    useEffect(() => {
        setActions(null);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadId, version]);

    // Clicar num horário da agenda é a intenção de marcar ali: abre o formulário já com a hora.
    useEffect(() => {
        if (!suggestedTime) return;

        setWhen(suggestedTime);
        setCreating(true);
    }, [suggestedTime]);

    const cancel = () => {
        setCreating(false);
        setText('');
        setWhen('');
        setCreateMeet(false);
        setAttempted(false);
    };

    const create = async () => {
        if (!text.trim() || !when) {
            setAttempted(true);
            return;
        }

        try {
            const res = await new CrmScheduleService().create({
                leadId,
                description: text.trim(),
                scheduledAt: new Date(when).toISOString(),
                createMeet: googleReady && createMeet,
            });

            const warning = calendarWarning(googleReady, createMeet, res.data.data);
            if (warning) showAlert(t(warning), 'warning', 5000);

            setText('');
            setWhen('');
            setCreateMeet(false);
            setAttempted(false);
            setCreating(false);
            onChanged();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const complete = async (action: LeadScheduleResponse) => {
        try {
            await new CrmScheduleService().close(action.id, { outcome: ScheduleOutcome.Completed });

            // Sem este aviso, a linha some e a pessoa fica sem saber se concluiu ou perdeu.
            showAlert(t('telaLead.concluidaFoiProHistorico'), 'success', 3000);
            onChanged();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    // Só as abertas: concluída vira registro no histórico, e manter aqui duplicaria o fato.
    const openActions = (actions ?? []).filter((action) => !action.closedAt);

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
            '&.Mui-error fieldset': { borderColor: 'error.main' },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary, fontSize: 12.5 },
    };

    return (
        <LeadBlock
            title={t('telaLead.acoes')}
            count={actions ? t('telaLead.abertas', { total: openActions.length }) : undefined}
        >
            {actions === null ? (
                <Skeleton variant="rounded" height={120} />
            ) : (
                <>
                    {openActions.length === 0 && !creating && (
                        <Typography
                            sx={{ fontSize: 12.5, color: cc.textSecondary, py: 1, display: 'block' }}
                        >
                            {t('telaLead.semAcaoAberta')}
                        </Typography>
                    )}

                    {openActions.map((action) => {
                        const overdue = new Date(action.scheduledAt) < new Date();

                        return (
                            <Box
                                key={action.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setEditing(action)}
                                onKeyDown={(e) => e.key === 'Enter' && setEditing(action)}
                                sx={{
                                    display: 'flex',
                                    gap: 1.25,
                                    p: 1.25,
                                    mb: 1,
                                    border: `1px solid ${cc.border}`,
                                    borderRadius: '9px',
                                    bgcolor: cc.surfaceBg,
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: cc.borderStrong },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: '8px',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: overdue
                                            ? `${cc.eventDanger}29`
                                            : cc.accentSoftBg,
                                        color: overdue ? cc.eventDanger : cc.accent,
                                    }}
                                >
                                    <EventAvailableIcon sx={{ fontSize: 15 }} />
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            color: cc.textPrimary,
                                        }}
                                    >
                                        {action.description}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            mt: 0.25,
                                            fontWeight: overdue ? 700 : 400,
                                            color: overdue ? cc.eventDanger : cc.textSecondary,
                                        }}
                                    >
                                        {overdue ? `${t('telaLead.atrasada')} — ` : ''}
                                        {formatCrmDateTime(action.scheduledAt)}
                                        {action.userName ? ` · ${action.userName}` : ''}
                                    </Typography>
                                    {action.meetUrl && (
                                        <Typography
                                            component="a"
                                            href={action.meetUrl}
                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mt: 0.4,
                                                fontSize: 11.5,
                                                fontWeight: 700,
                                                color: cc.accent,
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' },
                                            }}
                                        >
                                            <VideocamIcon sx={{ fontSize: 14 }} />
                                            {t('googleAgenda.entrarNoMeet')}
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        // O botão está dentro da linha, que abre a edição.
                                        e.stopPropagation();
                                        complete(action);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return;
                                        e.stopPropagation();
                                        complete(action);
                                    }}
                                    sx={{
                                        alignSelf: 'center',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: cc.textSecondary,
                                        border: `1px solid ${cc.border}`,
                                        borderRadius: '7px',
                                        px: 1.1,
                                        py: 0.5,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t('telaLead.concluir')}
                                </Box>
                            </Box>
                        );
                    })}

                    {creating ? (
                        <Box sx={{ display: 'grid', gap: 1, mt: 0.5 }}>
                            <TextField
                                size="small"
                                autoFocus
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                error={attempted && !text.trim()}
                                helperText={attempted && !text.trim() ? t('validacao.obrigatorio') : undefined}
                                placeholder={t('telaLead.oQueFazer')}
                                inputProps={{ maxLength: 200 }}
                                sx={field}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    type="datetime-local"
                                    value={when}
                                    onChange={(e) => setWhen(e.target.value)}
                                    error={attempted && !when}
                                    helperText={attempted && !when ? t('validacao.obrigatorio') : undefined}
                                    sx={{ ...field, flex: 1 }}
                                />
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    onClick={create}
                                    onKeyDown={(e) => e.key === 'Enter' && create()}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1.75,
                                        borderRadius: '8px',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        color: cc.onTint,
                                        border: `1px solid ${cc.eventSuccess}66`,
                                        bgcolor: `${cc.eventSuccess}22`,
                                    }}
                                >
                                    {t('general.salvar')}
                                </Box>

                                {/* Sem isto, o formulário só tinha saída salvando alguma coisa. */}
                                <Tooltip title={t('general.cancelar')}>
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        onClick={cancel}
                                        onKeyDown={(e) => e.key === 'Enter' && cancel()}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 1.1,
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            color: cc.textSecondary,
                                            border: `1px solid ${cc.border}`,
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                </Tooltip>
                            </Box>

                            {googleReady && <AgendaMeetOption checked={createMeet} onChange={setCreateMeet} />}
                        </Box>
                    ) : (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => setCreating(true)}
                            onKeyDown={(e) => e.key === 'Enter' && setCreating(true)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.75,
                                height: 36,
                                mt: 0.5,
                                border: `1px dashed ${cc.borderStrong}`,
                                borderRadius: '9px',
                                color: cc.textSecondary,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            <AddIcon sx={{ fontSize: 15 }} />
                            {t('telaLead.novaAcao')}
                        </Box>
                    )}
                </>
            )}
            <LeadActionModal action={editing} onClose={() => setEditing(null)} onSaved={onChanged} />
        </LeadBlock>
    );
};
